/**
 * Reddit Advertising Error Parser (Story 19.10, Task 2)
 *
 * Maps Reddit Ads API v3 error responses to standardized error types.
 *
 * Reddit surfaces errors in two REST envelope shapes:
 *   - Validation / structured: `{ "errors": [{ "code": "...", "message": "...", "field": "..." }] }`
 *   - OAuth-style: `{ "error": "...", "message": "..." }`
 *
 * The parser inspects HTTP status first (the cheapest signal). 403 responses
 * trigger AC #13 sales-gating detection via a **two-tier matching strategy**
 * (Round 1 Senior Dev insight — fragile-regex hardening):
 *   - **Tier 1 (structured-code match):** if `errors[0].code` matches a known
 *     Reddit access-denied code such as `"ADS_API_ACCESS_DENIED"`,
 *     `"ADS_API_NOT_ENABLED"`, `"INSUFFICIENT_ADS_API_PRIVILEGE"`.
 *   - **Tier 2 (message-substring fallback):** if `errors[0].message` (or
 *     `message` / `error_description`) matches `/Ads API|API access|gated|not enabled/i`.
 *
 * Either tier maps the error to `api_not_enabled` so the cron action is
 * `mark_revoked_notify_user` (not silently retried as a permission_denied).
 */

// ============================================================================
// Types
// ============================================================================

export type RedditAdsErrorType =
  | "token_expired"
  | "permission_denied"
  | "api_not_enabled"
  | "not_found"
  | "rate_limited"
  | "provider_unavailable"
  | "unknown";

export interface RedditAdsApiError {
  type: RedditAdsErrorType;
  message: string;
  status: number | null;
  retryable: boolean;
}

interface RedditError {
  code?: string;
  message?: string;
  field?: string;
}

interface RestErrorEnvelope {
  errors?: RedditError[];
  /** OAuth-style fallback */
  error?: string;
  error_description?: string;
  message?: string;
}

// ============================================================================
// Status Code Mapping
// ============================================================================

const STATUS_CODE_MAP: Record<
  number,
  { type: RedditAdsErrorType; retryable: boolean }
> = {
  401: { type: "token_expired", retryable: false },
  // 403 is handled below — the AC #13 sales-gating tier-1/tier-2 match runs
  // before falling back to permission_denied.
  404: { type: "not_found", retryable: false },
  429: { type: "rate_limited", retryable: true },
  500: { type: "provider_unavailable", retryable: true },
  502: { type: "provider_unavailable", retryable: true },
  503: { type: "provider_unavailable", retryable: true },
  504: { type: "provider_unavailable", retryable: true },
};

// ============================================================================
// AC #13 — Sales-gating recognition
// ============================================================================

/**
 * Known structured error codes Reddit emits for Ads-API-not-enabled scenarios.
 * Verify exact codes against the OpenAPI `Error` schema during impl; if Reddit
 * doesn't emit a structured code we degrade to the secondary tier.
 */
const ADS_API_NOT_ENABLED_CODES = new Set<string>([
  "ADS_API_ACCESS_DENIED",
  "ADS_API_NOT_ENABLED",
  "INSUFFICIENT_ADS_API_PRIVILEGE",
]);

/**
 * Secondary message-substring fallback regex. Used when Reddit doesn't emit a
 * structured `errors[0].code` but the `message` text indicates Ads-API gating.
 *
 * Round 1 Senior Dev insight: regex-only matching is fragile, so we layer it
 * BELOW the structured-code tier — only relevant when Reddit returns a 403
 * without a recognised code.
 */
const ADS_API_NOT_ENABLED_MESSAGE_REGEX = /Ads API|API access|gated|not enabled/i;

/**
 * Returns true when the error envelope (or fallback message) indicates AC #13
 * Reddit Ads API access gating.
 *
 * @internal exported for direct unit testing only
 */
export function isAdsApiNotEnabledError(body: RestErrorEnvelope): boolean {
  // Tier 1: structured-code match
  const errors = Array.isArray(body.errors) ? body.errors : [];
  for (const e of errors) {
    if (typeof e.code === "string" && ADS_API_NOT_ENABLED_CODES.has(e.code)) {
      return true;
    }
  }

  // Tier 2: message-substring fallback
  const candidates = [
    errors[0]?.message,
    body.message,
    body.error_description,
    body.error,
  ];
  for (const m of candidates) {
    if (typeof m === "string" && ADS_API_NOT_ENABLED_MESSAGE_REGEX.test(m)) {
      return true;
    }
  }
  return false;
}

// ============================================================================
// Error Parser
// ============================================================================

/**
 * Parses a Reddit Ads error response into a standardized error object.
 *
 * Resolution order:
 *   1. HTTP status (fastest, most reliable)
 *   2. AC #13 — 403 with sales-gating signal → `api_not_enabled`
 *   3. 403 fallback → `permission_denied`
 *   4. REST envelope `message` (or `error_description` for OAuth-style errors)
 *   5. Fallback to "unknown"
 *
 * @returns The mapped error, or null when the input is empty/successful
 *          (HTTP 2xx with no error body).
 */
export function parseRedditAdsApiError(
  responseBody: unknown,
  httpStatus?: number
): RedditAdsApiError | null {
  const body = (responseBody ?? {}) as RestErrorEnvelope;

  // ---- 1. AC #13 — 403 sales-gating mapping ---------------------------------
  if (httpStatus === 403) {
    if (isAdsApiNotEnabledError(body)) {
      return {
        type: "api_not_enabled",
        message: extractErrorMessage(body, 403),
        status: 403,
        retryable: false,
      };
    }
    return {
      type: "permission_denied",
      message: extractErrorMessage(body, 403),
      status: 403,
      retryable: false,
    };
  }

  // ---- 2. HTTP status mapping -----------------------------------------------
  if (httpStatus && (httpStatus < 200 || httpStatus >= 300)) {
    const mapping = STATUS_CODE_MAP[httpStatus];
    if (mapping) {
      const message = extractErrorMessage(body, httpStatus);
      return {
        type: mapping.type,
        message,
        status: httpStatus,
        retryable: mapping.retryable,
      };
    }
  }

  // ---- 3. REST envelope without explicit code mapping -----------------------
  if (Array.isArray(body.errors) && body.errors[0]?.message) {
    return {
      type: "unknown",
      message: body.errors[0].message,
      status: httpStatus ?? null,
      retryable: false,
    };
  }
  if (body.error_description) {
    return {
      type: "unknown",
      message: body.error_description,
      status: httpStatus ?? null,
      retryable: false,
    };
  }
  if (body.message) {
    return {
      type: "unknown",
      message: body.message,
      status: httpStatus ?? null,
      retryable: false,
    };
  }

  // ---- 4. No identifiable error ----------------------------------------------
  if (!httpStatus || (httpStatus >= 200 && httpStatus < 300)) {
    return null;
  }

  return {
    type: "unknown",
    message: extractErrorMessage(body, httpStatus),
    status: httpStatus,
    retryable: false,
  };
}

// ============================================================================
// Typed error class
// ============================================================================

/**
 * Thrown by Reddit REST helpers when Reddit returns a non-2xx response.
 * Carries the parsed error type so the adapter can route it to the correct
 * `AdapterErrorCode` without string-matching the message.
 */
export class RedditAdsApiCallError extends Error {
  readonly type: RedditAdsErrorType;
  readonly status: number | null;
  readonly retryable: boolean;
  constructor(parsed: RedditAdsApiError, prefix: string) {
    super(`${prefix}: ${parsed.message}`);
    this.name = "RedditAdsApiCallError";
    this.type = parsed.type;
    this.status = parsed.status;
    this.retryable = parsed.retryable;
  }
}

/**
 * Maps a Reddit Ads error type to the corresponding `AdapterErrorCode`.
 * Used by the adapter so we don't string-match localised provider messages.
 *
 * Note: 404 → `invalid_response` (NOT `account_not_found`) because Reddit 404
 * during onboarding means "endpoint URL changed" or "schema drift" — the
 * benign onboarding case (zero ad accounts) is checked explicitly by the
 * caller before throwing. Mirrors Snapchat 19.7 review #3 M2.
 */
export function redditAdsErrorTypeToAdapterCode(
  type: RedditAdsErrorType
):
  | "token_expired"
  | "permission_denied"
  | "api_not_enabled"
  | "rate_limited"
  | "provider_unavailable"
  | "invalid_response"
  | "account_not_found" {
  switch (type) {
    case "token_expired":
      return "token_expired";
    case "permission_denied":
      return "permission_denied";
    case "api_not_enabled":
      return "api_not_enabled";
    case "not_found":
      return "invalid_response";
    case "rate_limited":
      return "rate_limited";
    case "provider_unavailable":
      return "provider_unavailable";
    case "unknown":
    default:
      return "invalid_response";
  }
}

/**
 * Pulls the most descriptive message we can find out of a Reddit error
 * envelope, with a sensible fallback when nothing structured is present.
 */
function extractErrorMessage(body: RestErrorEnvelope, status: number): string {
  if (Array.isArray(body.errors) && body.errors[0]?.message) {
    return body.errors[0].message;
  }
  if (body.error_description) return body.error_description;
  if (body.message) return body.message;
  if (body.error) return body.error;
  if (Array.isArray(body.errors) && body.errors[0]?.code) {
    return `Reddit Ads error: ${body.errors[0].code}`;
  }
  return `Reddit Ads API error: HTTP ${status}`;
}
